import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, Volume2 } from "lucide-react";
import { motion } from "framer-motion";

interface TermCardProps {
  id: string;
  word: string;
  meaningEnglish?: string | null;
  meaningHindi?: string | null;
  meaningGujarati?: string | null;
  pronunciation?: string | null;
  index?: number;
}

export function TermCard({
  id,
  word,
  meaningEnglish,
  meaningHindi,
  meaningGujarati,
  pronunciation,
  index = 0,
}: TermCardProps) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const primaryMeaning =
    language === "hi"
      ? meaningHindi || meaningEnglish || meaningGujarati
      : language === "gu"
        ? meaningGujarati || meaningEnglish || meaningHindi
        : meaningEnglish || meaningHindi || meaningGujarati;

  const truncate = (text: string | null | undefined, len = 80) => {
    if (!text) {
      return null;
    }
    return text.length > len ? text.slice(0, len) + "…" : text;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.4,
        delay: index * 0.06,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      <Card
        onClick={() => navigate(`/term/${id}`)}
        className="border-border/40 hover:border-primary/30 group h-full cursor-pointer overflow-hidden bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
      >
        {/* Top accent */}
        <div className="from-primary/60 via-gold/40 to-primary/60 h-0.5 bg-gradient-to-r opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        <CardHeader className="px-5 pb-2 pt-5">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-foreground group-hover:text-primary line-clamp-1 font-serif text-xl transition-colors duration-200 md:text-2xl">
              {word}
            </CardTitle>
            <Badge
              variant="secondary"
              className="bg-primary/10 text-primary group-hover:bg-primary shrink-0 border-0 text-sm font-bold transition-all duration-300 group-hover:text-white"
            >
              {word.charAt(0).toUpperCase()}
            </Badge>
          </div>
          {pronunciation && (
            <div className="text-muted-foreground mt-1 flex items-center gap-2">
              <Volume2 className="h-3.5 w-3.5" />
              <p className="text-base italic">/{pronunciation}/</p>
            </div>
          )}
        </CardHeader>

        <CardContent className="px-5 pb-5 pt-1">
          {primaryMeaning && (
            <p className="text-foreground/80 line-clamp-3 text-sm leading-relaxed">
              {truncate(primaryMeaning, 150)}
            </p>
          )}
          <div className="flex items-center justify-end pt-3">
            <span className="text-muted-foreground group-hover:text-primary mr-1 text-xs transition-colors">
              {t("termCard.viewAllLanguages")}
            </span>
            <ArrowRight className="text-muted-foreground group-hover:text-primary h-4 w-4 transition-all duration-300 group-hover:translate-x-1" />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
